import { useCallback, useSyncExternalStore } from "react";

/**
 * Three states, not two.
 *
 * An explicit choice stamps `data-theme` on the root; the default "system" stamps nothing
 * and lets `prefers-color-scheme` decide. Tokens are defined for all three, so a page
 * renders correctly with no stamp at all.
 *
 * Dark mode is not optional here — agents work evening shifts. docs/03 §4.
 */
export type Theme = "system" | "light" | "dark";

const KEY = "mp_theme";
const listeners = new Set<() => void>();

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    // Private windows and blocked site data throw on access, not on read of a missing key.
    return "system";
  }
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

export function setTheme(theme: Theme): void {
  try {
    if (theme === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    // A per-viewer convenience, not state worth failing over.
  }
  apply(theme);
  for (const l of listeners) l();
}

/** Call once at startup, before first paint, so there is no flash of the wrong theme. */
export function initTheme(): void {
  apply(read());
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, read, () => "system" as Theme);
  return [theme, useCallback(setTheme, [])];
}

/** Cycles system → light → dark → system, which is the order a toggle should walk. */
export function nextTheme(current: Theme): Theme {
  return current === "system" ? "light" : current === "light" ? "dark" : "system";
}
